// src/data.js

// 辅助函数：解析CSV文本
function normalizeCSVCell(cell = '') {
  let value = cell.replace(/^\uFEFF/, '').trim();
  if (value.startsWith('"') && value.endsWith('"')) {
    value = value.slice(1, -1).replace(/""/g, '"');
  }
  return value;
}

function splitCSVLine(line) {
  const cells = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      cells.push(normalizeCSVCell(current));
      current = '';
    } else {
      current += char;
    }
  }

  cells.push(normalizeCSVCell(current));
  return cells;
}

function parseCSV(csvText) {
  const lines = csvText
    .replace(/^\uFEFF/, '')
    .trim()
    .split(/\r?\n/);

  if (lines.length < 2) return [];

  const headers = splitCSVLine(lines[0]);
  const rows = lines.slice(1).map(line => {
    const values = splitCSVLine(line);
    const rowObject = {};
    headers.forEach((header, index) => {
      rowObject[header] = values[index] || '';
    });
    return rowObject;
  });

  return rows.filter(row => row.scenic_name);
}

// Predefined image data. Category from CSV will be prioritized.
const predefinedImageData = {
  "青海湖（青海）": { image: "https://images.pexels.com/photos/572897/pexels-photo-572897.jpeg" }, 
  "周庄（江苏）": { image: "https://images.pexels.com/photos/372166/pexels-photo-372166.jpeg" },
  "神农架（湖北）": { image: "https://images.pexels.com/photos/161401/bg-scenic-earthquake-in-the-sky-161401.jpeg" },
  "颐和园（北京）": { image: "https://images.pexels.com/photos/372166/pexels-photo-372166.jpeg" },
  "杭州西湖（浙江）": { image: "https://images.pexels.com/photos/249798/pexels-photo-249798.jpeg" },
  "故宫（北京）": { image: "https://images.pexels.com/photos/4215113/pexels-photo-4215113.jpeg" },
  "万里长城（八达岭/慕田峪等）": { image: "https://images.pexels.com/photos/2440021/pexels-photo-2440021.jpeg" }, // Adjusted name to match common CSV entry
  "万里长城（八达岭）": { image: "https://images.pexels.com/photos/2440021/pexels-photo-2440021.jpeg" }, // Kept for broader matching
  "秦始皇兵马俑（陕西西安）": { image: "https://images.pexels.com/photos/1666021/pexels-photo-1666021.jpeg" },
  // Add more images as needed, matching the scenic_name from your CSV
};
const defaultImage = "https://images.pexels.com/photos/208701/pexels-photo-208701.jpeg";
const defaultCategoryNameFromCode = "未分类景区"; // Fallback if CSV has no category


function calculateDimensionRanks(areas) {
  const dimensions = ['BM', 'Exc', 'Hyg', 'PT', 'REP', 'TSA', 'TSH', 'TT'];
  const areasCopy = areas.map(area => ({ ...area, dimensionRanks: {} }));

  dimensions.forEach(dim => {
    const validAreasForDim = areasCopy
      .filter(area => area.scores && typeof area.scores[dim] === 'number')
      .map(area => ({ id: area.id, name: area.name, score: area.scores[dim] }));

    validAreasForDim.sort((a, b) => b.score - a.score);

    let rank = 0;
    let lastScore = -Infinity;
    validAreasForDim.forEach((sortedArea, index) => {
      if (sortedArea.score !== lastScore) {
        rank = index + 1;
        lastScore = sortedArea.score;
      }
      const targetArea = areasCopy.find(a => a.id === sortedArea.id);
      if (targetArea) {
        targetArea.dimensionRanks[dim] = rank;
      }
    });

    areasCopy.forEach(area => {
      if (!(area.scores && typeof area.scores[dim] === 'number') || area.dimensionRanks[dim] === undefined) {
        area.dimensionRanks[dim] = "N/A";
      }
    });
  });
  return areasCopy;
}

export async function loadScenicData() {
  try {
    const cacheBuster = Date.now();
    const response = await fetch(`${process.env.PUBLIC_URL}/8.final_summary_scores_aligned_to_rank_3.csv?v=${cacheBuster}`);
    if (!response.ok) {
      const errorMsg = 'HTTP error! status: ' + response.status + ' when fetching CSV.';
      console.error(errorMsg);
      throw new Error('HTTP error! status: ' + response.status);
    }
    const csvText = await response.text();
    const csvData = parseCSV(csvText);
    
    if (!csvData || csvData.length === 0) {
        console.warn("CSV data is empty or parsing failed. Returning empty data.");
        return { scenicAreas: [], scenicAreasByCategory: {} };
    }

    let scenicAreasFromCSV = csvData.map((row, index) => {
      const name = row.scenic_name; 
      const predefined = predefinedImageData[name] || {};
      const categoryFromCSV = row.Category ? row.Category.trim() : defaultCategoryNameFromCode;

      const getScore = (value) => {
        const num = parseFloat(value);
        return !isNaN(num) ? num : "数据暂无";
      };

      const scores = {
        BM: getScore(row.BM_weighted),
        Exc: getScore(row.Exc_weighted),
        Hyg: getScore(row.Hyg_weighted),
        PT: getScore(row.PT_weighted),
        REP: getScore(row.REP_weighted),
        TSA: getScore(row.TSA_weighted),
        TSH: getScore(row.TSH_weighted),
        TT: getScore(row.TT_weighted),
      };
      
      const hasInvalidScore = Object.values(scores).some(s => s === "数据暂无");
      const overallRank = parseFloat(row.Rank);

      // 优先使用CSV中的图片URL，如果没有则使用预定义的图片，最后使用默认图片
      const imageUrl = row.image_url || predefined.image || defaultImage;

      return {
        id: index + 1, 
        name: name || "未知景区",
        category: categoryFromCSV,
        image: imageUrl,
        rank: !isNaN(overallRank) && !hasInvalidScore ? overallRank : "数据暂无",
        scores: scores, 
        dimensionRanks: {} 
      };
    });

    scenicAreasFromCSV = calculateDimensionRanks(scenicAreasFromCSV);

    const scenicAreasByCategory = scenicAreasFromCSV.reduce((acc, area) => {
      const category = area.category; // Already determined from CSV or default
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(area);
      return acc;
    }, {});

    return { scenicAreas: scenicAreasFromCSV, scenicAreasByCategory };

  } catch (error) {
    console.error("Failed to load or process scenic data:", error);
    return { scenicAreas: [], scenicAreasByCategory: {} };
  }
} 
